import { baseApi } from '../../services/baseApi';
import { ApiEndPoints } from '../../constants/apiEndPoints';

/* ------------------------------------------------------------------ *
 * Types
 * ------------------------------------------------------------------ */

/** An assignee as returned on a citizen request / task. */
export interface CrAssignee {
  id: number;
  full_name?: string | null;
  name?: string | null;
}

/** A file attached to a citizen request (posted + read back). */
export interface CrAsset {
  id?: number;
  file_url?: string;
  file_type?: string;
  name?: string;
}

/** A citizen request row (list) / object (detail). */
export interface CitizenRequest {
  id: number;
  cr_code?: string | null;
  visitor_name?: string | null;
  visitor_phone?: string | null;
  visitor_address?: string | null;
  location?: string | null;
  issue_type?: string | null; // Category
  issue_type_name?: string | null;
  category?: string | null;
  cr_category?: string | null; // CR Type
  priority?: string | null;
  status?: string | null;
  source?: string | null;
  department?: string | null;
  description?: string | null;
  /** Request received date. */
  date_received?: string | null;
  tasks_count?: number | null;
  assignees?: CrAssignee[] | null;
  tasks?: CitizenRequestTask[] | null;
  assets?: CrAsset[] | null;
  created_at?: string | null;
  details?: Record<string, unknown> | null;
}

/** Summary counts for the list-page stat cards. */
export interface CitizenRequestStats {
  critical?: number;
  critical_today?: number;
  active?: number;
  processing_pct?: number;
  completed_mtd?: number;
  avg_response_time?: string | number;
  total?: number;
  [key: string]: number | string | undefined;
}

/** Request body for POST/PATCH /citizen-request/ — a single call carries the nested tasks. */
export interface CreateCitizenRequestPayload {
  fullName?: string;
  email?: string;
  phone?: string;
  receivedDate?: string;
  address?: string;
  district?: string;
  description?: string;
  crType?: string;
  priority?: string;
  /** 'draft' | 'complete'. */
  status?: string;
  reference_cr?: string;
  is_duplicate?: boolean;
  reference_person?: {
    scope?: string;
    designation?: string;
    phone?: string;
    phones?: string[];
    /** Within-constituency: a picked person sends its id + source. */
    user?: number;
    source?: string;
    /** Outside/NCR (or a typed custom value): the person's name. */
    name?: string;
    /** Selected location — a node id (contract) or an EC-tree code. */
    location?: number | string;
  };
  assignees?: number[];
  assets?: CrAsset[];
  tasks?: CreateCrTaskPayload[];
}

/** A task under a citizen request (Step 2). */
export interface CitizenRequestTask {
  id: number;
  category?: string | null;
  subCategory?: string | null;
  sub_category?: string | null;
  tags?: string[] | null;
  priority?: string | null;
  status?: string | null;
  dueDate?: string | null;
  due_date?: string | null;
  /** Task due date (read model). */
  scheduled_date?: string | null;
  description?: string | null;
  assignMember?: number | null;
  assignMembers?: number[] | null;
  /** Teams (read model) — objects with a `primary` flag. */
  teams?: Array<{ id: number; primary?: boolean }> | null;
  /** Primary assignee (read model). */
  assigned_user?: number | null;
  assigned_user_name?: string | null;
  /** Team members (read model) — ids or nested user objects. */
  assignees?: Array<number | { id?: number; full_name?: string | null; name?: string | null }> | null;
  assignee_name?: string | null;
  department?: string | null;
  /** Nested sub-tasks (read model). */
  subtasks?: CitizenRequestSubTask[] | null;
}

/** A sub-task nested under a task (read model). */
export interface CitizenRequestSubTask {
  id?: number;
  name?: string | null;
  assigned_user?: number | null;
  assigned_user_name?: string | null;
  scheduled_date?: string | null;
  task_status?: string | null;
  assignees?: Array<{ id?: number; full_name?: string | null; name?: string | null }> | null;
  attachment?: CrAsset | null;
}

/** A team entry on a task — one may be flagged primary. */
export interface CrTeam {
  id: number;
  primary?: boolean;
}

/** A sub-task carried inside a task in the citizen-request payload. */
export interface CreateCrSubTaskPayload {
  name: string;
  assigneeId?: number;
  dueDate?: string;
  status?: string;
  attachment?: CrAsset;
}

/** A task carried inside the citizen-request payload (or posted to the tasks endpoint). */
export interface CreateCrTaskPayload {
  category: string;
  subCategory?: string;
  priority?: string;
  dueDate?: string;
  description?: string;
  location?: string;
  status?: string;
  /** Old-CR reference on this task. */
  reference_cr?: string;
  is_duplicate?: boolean;
  /** Individual members assigned to the task. */
  assignMembers?: number[];
  /** Teams assigned to the task; one may be marked `primary`. */
  teams?: CrTeam[];
  /** Assigned sub-tasks nested under this task. */
  subTasks?: CreateCrSubTaskPayload[];
}

/** A related/duplicate CR returned by the /related/ scan (fields best-effort / optional). */
export interface CrRelatedHit {
  id: number;
  cr_code?: string | null;
  category?: string | null;
  sub_category?: string | null;
  area_code?: string | null;
  area_name?: string | null;
  location_name?: string | null;
  description?: string | null;
  subject?: string | null;
  repeat_count?: number | null;
  letter_repeat_frequency?: string | null;
  status?: string | null;
}

/** A hit from the old-CR reference search. */
export interface CrReferenceHit {
  id: number;
  cr_code?: string | null;
  subject?: string | null;
  status?: string | null;
  created_at?: string | null;
}

/** A user offered in the task assignee dropdowns. */
export interface AssignableUser {
  id: number;
  full_name?: string | null;
  name?: string | null;
}

/** A category / sub-category option (normalized from strings or objects). */
export interface CrCategoryOption {
  value: string;
  label: string;
}
/** Raw category row — a bare string or an object with a few possible name keys. */
type CrCategoryLike =
  | string
  | {
      id?: number | string;
      value?: string;
      name?: string;
      label?: string;
      category?: string;
      code?: string;
    };

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

type Listish<T> = T[] | { count?: number; results?: T[] };
const asList = <T>(r: Listish<T>): T[] => (Array.isArray(r) ? r : (r?.results ?? []));

export interface Paginated<T> {
  items: T[];
  total: number;
}
const toPaginated = <T>(r: Listish<T>): Paginated<T> => {
  const items = asList(r);
  const total = Array.isArray(r) ? r.length : (r?.count ?? items.length);
  return { items, total };
};

/* Defensive accessors — field names aren't guaranteed across list/detail. */
export const crCode = (c: CitizenRequest) => c.cr_code || `CR-${c.id}`;
export const crName = (c: CitizenRequest) => c.visitor_name || '—';
export const crPhone = (c: CitizenRequest) => c.visitor_phone || '';
export const crLocation = (c: CitizenRequest) => c.location || '';
export const crCategory = (c: CitizenRequest) =>
  c.issue_type || c.issue_type_name || c.category || '';
export const crAssigneeNames = (c: CitizenRequest): string[] =>
  (c.assignees ?? []).map((a) => a.full_name || a.name || '').filter(Boolean);
export const crTaskSubCategory = (t: CitizenRequestTask) =>
  t.subCategory || t.sub_category || (Array.isArray(t.tags) ? t.tags[0] : '') || '';

/* ------------------------------------------------------------------ *
 * Endpoints
 * ------------------------------------------------------------------ */

export interface CrListArgs {
  search?: string;
  status?: string;
  priority?: string;
  issue_type?: string;
  cr_category?: string;
  source?: string;
  department?: string;
  ordering?: string;
  page?: number;
  pageSize?: number;
}

export const citizenRequestApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getCitizenRequests: build.query<Paginated<CitizenRequest>, CrListArgs | void>({
      query: (arg) => ({
        url: `${ApiEndPoints.citizenRequest}/`,
        params: {
          ...(arg?.search ? { search: arg.search } : {}),
          ...(arg?.status ? { status: arg.status } : {}),
          ...(arg?.priority ? { priority: arg.priority } : {}),
          ...(arg?.issue_type ? { issue_type: arg.issue_type } : {}),
          ...(arg?.cr_category ? { cr_category: arg.cr_category } : {}),
          ...(arg?.source ? { source: arg.source } : {}),
          ...(arg?.department ? { department: arg.department } : {}),
          ...(arg?.ordering ? { ordering: arg.ordering } : {}),
          page: arg?.page ?? 1,
          page_size: arg?.pageSize ?? 10,
        },
      }),
      transformResponse: (r: Listish<CitizenRequest>) => toPaginated(r),
      providesTags: ['CitizenRequests'],
    }),

    getCitizenRequestStats: build.query<CitizenRequestStats, { search?: string } | void>({
      query: (arg) => ({
        url: `${ApiEndPoints.citizenRequestStats}/`,
        params: arg?.search ? { search: arg.search } : {},
      }),
      providesTags: ['CitizenRequests'],
    }),

    getCitizenRequest: build.query<CitizenRequest, number | string>({
      query: (id) => ({ url: `${ApiEndPoints.citizenRequest}/${id}/` }),
      providesTags: ['CitizenRequests'],
    }),

    createCitizenRequest: build.mutation<CitizenRequest, CreateCitizenRequestPayload>({
      query: (body) => ({ url: `${ApiEndPoints.citizenRequest}/`, method: 'POST', body }),
      // The request carries nested tasks, so refresh the tasks list too.
      invalidatesTags: ['CitizenRequests', 'CitizenRequestTasks'],
    }),

    updateCitizenRequest: build.mutation<
      CitizenRequest,
      { id: number | string; patch: Partial<CreateCitizenRequestPayload> }
    >({
      query: ({ id, patch }) => ({
        url: `${ApiEndPoints.citizenRequest}/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      // Nested tasks may change on update — refresh the tasks list too.
      invalidatesTags: ['CitizenRequests', 'CitizenRequestTasks'],
    }),

    deleteCitizenRequest: build.mutation<void, number | string>({
      query: (id) => ({ url: `${ApiEndPoints.citizenRequest}/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['CitizenRequests'],
    }),

    // --- Tasks (Step 2) ---
    getCrTasks: build.query<
      Paginated<CitizenRequestTask>,
      {
        id: number | string;
        search?: string;
        task_status?: string;
        category?: string;
        assigned?: string;
        date?: string;
        page?: number;
        pageSize?: number;
        /** Fetch the whole list without pagination (Step-2 task list). */
        all?: boolean;
      }
    >({
      query: ({ id, search, task_status, category, assigned, date, page, pageSize, all }) => ({
        url: `${ApiEndPoints.citizenRequest}/${id}/tasks/`,
        params: {
          ...(search ? { search } : {}),
          ...(task_status && task_status !== 'All' ? { task_status } : {}),
          ...(category && category !== 'All' ? { category } : {}),
          ...(assigned && assigned !== 'All' ? { assigned } : {}),
          ...(date && date !== 'All' ? { date } : {}),
          ...(all ? {} : { page: page ?? 1, page_size: pageSize ?? 10 }),
        },
      }),
      transformResponse: (r: Listish<CitizenRequestTask>) => toPaginated(r),
      providesTags: ['CitizenRequestTasks'],
    }),

    createCrTask: build.mutation<
      CitizenRequestTask,
      { id: number | string; body: CreateCrTaskPayload }
    >({
      query: ({ id, body }) => ({
        url: `${ApiEndPoints.citizenRequest}/${id}/tasks/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CitizenRequestTasks', 'CitizenRequests'],
    }),

    // Task update/delete go through the top-level task resource (/task/{id}/).
    updateCrTask: build.mutation<
      CitizenRequestTask,
      { taskId: number | string; body: Partial<CreateCrTaskPayload> }
    >({
      query: ({ taskId, body }) => ({
        url: `${ApiEndPoints.task}/${taskId}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['CitizenRequestTasks', 'CitizenRequests'],
    }),

    deleteCrTask: build.mutation<void, { taskId: number | string }>({
      query: ({ taskId }) => ({
        url: `${ApiEndPoints.task}/${taskId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CitizenRequestTasks', 'CitizenRequests'],
    }),

    // --- Assignable users (scoped to the CR's area) ---
    getAssignableUsers: build.query<AssignableUser[], { id: number | string }>({
      query: ({ id }) => ({ url: `${ApiEndPoints.citizenRequest}/${id}/assignable_users/` }),
      transformResponse: (r: Listish<AssignableUser>) => asList(r),
    }),

    // --- Old CR reference search ---
    searchOldCr: build.query<CrReferenceHit[], { q: string }>({
      query: ({ q }) => ({ url: `${ApiEndPoints.citizenRequestReferenceSearch}/`, params: { q } }),
      transformResponse: (r: Listish<CrReferenceHit>) => asList(r),
    }),

    // --- Related / duplicate scan (Step 2 "Related Issue" panel) ---
    searchRelatedCr: build.query<
      CrRelatedHit[],
      {
        areaCode?: string;
        category?: string;
        subCategory?: string;
        q?: string;
        limit?: number;
        exclude?: string | number;
      }
    >({
      query: ({ areaCode, category, subCategory, q, limit, exclude }) => ({
        url: `${ApiEndPoints.citizenRequest}/related/`,
        params: {
          ...(areaCode ? { area_code: areaCode } : {}),
          ...(category ? { category } : {}),
          ...(subCategory ? { sub_category: subCategory } : {}),
          ...(q ? { q } : {}),
          ...(limit ? { limit } : {}),
          ...(exclude ? { exclude } : {}),
        },
      }),
      transformResponse: (r: Listish<CrRelatedHit>) => asList(r),
    }),

    // --- Category / Sub-category (no arg → categories; ?category=X → its sub-categories) ---
    getCrCategories: build.query<CrCategoryOption[], { category?: string } | void>({
      query: (arg) => ({
        url: `${ApiEndPoints.citizenRequest}/categories/`,
        params: arg && arg.category ? { category: arg.category } : {},
      }),
      transformResponse: (r: Listish<CrCategoryLike>) =>
        asList(r)
          .map((x) => {
            if (typeof x === 'string') return { value: x, label: x };
            const value = x.value ?? x.name ?? x.category ?? x.code ?? x.id;
            const label = x.name ?? x.label ?? x.category ?? x.value ?? value;
            return { value: String(value ?? ''), label: String(label ?? '') };
          })
          .filter((o) => o.value),
    }),
  }),
});

export const {
  useGetCitizenRequestsQuery,
  useGetCitizenRequestStatsQuery,
  useGetCitizenRequestQuery,
  useCreateCitizenRequestMutation,
  useUpdateCitizenRequestMutation,
  useDeleteCitizenRequestMutation,
  useGetCrTasksQuery,
  useCreateCrTaskMutation,
  useUpdateCrTaskMutation,
  useDeleteCrTaskMutation,
  useGetAssignableUsersQuery,
  useLazySearchOldCrQuery,
  useSearchRelatedCrQuery,
  useGetCrCategoriesQuery,
} = citizenRequestApi;
